import { MouseEvent } from "react";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import LoadingImage from "#/components/shared/LoadingImage";
import Typography from "#/components/shared/typography";
import { cn } from "#/components/utls/utils";
import { MediaItem } from "#/lib/mediaApi";
import { buildMediaUrl } from "#/lib/mediaUrls";

export type MediaTableProps = {
  items: MediaItem[];
  isLoading: boolean;
  selectedId?: string | null;
  onRowClick: (item: MediaItem) => void;
  onCopyUrl: (item: MediaItem) => void;
  onCopyImg: (item: MediaItem) => void;
  onCopyMarkdown: (item: MediaItem) => void;
};

const skeletonRows = Array.from({ length: 6 }, (_, index) => index);

function formatDate(value?: string | null) {
  if (!value) {
    return "—";
  }

  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) {
    return value;
  }

  return new Date(parsed).toLocaleString();
}

export function MediaTable({
  items,
  isLoading,
  selectedId,
  onRowClick,
  onCopyUrl,
  onCopyImg,
  onCopyMarkdown,
}: MediaTableProps) {
  const handleAction =
    (handler: (item: MediaItem) => void, item: MediaItem) =>
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      handler(item);
    };

  return (
    <div className="overflow-hidden rounded-2xl border border-cream-200 bg-white">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-cream-50 text-xs uppercase tracking-wide text-brown-500">
            <tr>
              <th scope="col" className="px-4 py-3 font-semibold">
                Thumbnail
              </th>
              <th scope="col" className="px-4 py-3 font-semibold">
                Name
              </th>
              <th scope="col" className="px-4 py-3 font-semibold">
                Slug
              </th>
              <th scope="col" className="px-4 py-3 font-semibold">
                Type
              </th>
              <th scope="col" className="px-4 py-3 font-semibold">
                Active
              </th>
              <th scope="col" className="px-4 py-3 font-semibold">
                Created
              </th>
              <th scope="col" className="px-4 py-3 font-semibold">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cream-100">
            {isLoading
              ? skeletonRows.map((row) => (
                  <tr key={`skeleton-${row}`} className="animate-pulse">
                    {Array.from({ length: 7 }).map((_, index) => (
                      <td key={`cell-${row}-${index}`} className="px-4 py-4">
                        <div className="h-4 w-full rounded bg-cream-200" />
                      </td>
                    ))}
                  </tr>
                ))
              : items.map((item) => {
                  const altText = item.name ?? item.slug ?? "Media thumbnail";
                  const imageUrl = buildMediaUrl(item.url ?? item.slug ?? "");
                  const isSelected = item.id === selectedId;

                  return (
                    <tr
                      key={item.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => onRowClick(item)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onRowClick(item);
                        }
                      }}
                      className={cn(
                        "cursor-pointer transition-colors",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-300",
                        isSelected
                          ? "bg-terracotta-50"
                          : "hover:bg-cream-50"
                      )}
                    >
                      <td className="px-4 py-3">
                        <div className="h-16 w-20">
                          <LoadingImage
                            src={imageUrl}
                            alt={altText}
                            className="rounded-md"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Typography
                          variant="body2"
                          className="max-w-[220px] truncate text-brown-800"
                        >
                          {item.name ?? "—"}
                        </Typography>
                      </td>
                      <td className="px-4 py-3">
                        <Typography variant="body2" className="text-brown-700">
                          {item.slug ?? "—"}
                        </Typography>
                      </td>
                      <td className="px-4 py-3">
                        <Typography variant="body2" className="text-brown-700">
                          {item.image_type ?? "—"}
                        </Typography>
                      </td>
                      <td className="px-4 py-3">
                        <Typography variant="body2" className="text-brown-700">
                          {item.is_active === null
                            ? "—"
                            : item.is_active
                            ? "Yes"
                            : "No"}
                        </Typography>
                      </td>
                      <td className="px-4 py-3">
                        <Typography variant="body2" className="text-brown-700">
                          {formatDate(item.created_at)}
                        </Typography>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <ActionButton
                            size="sm"
                            tone="secondary"
                            onClick={handleAction(onCopyUrl, item)}
                          >
                            Copy URL
                          </ActionButton>
                          <ActionButton
                            size="sm"
                            tone="secondary"
                            onClick={handleAction(onCopyImg, item)}
                          >
                            Copy &lt;img&gt;
                          </ActionButton>
                          <ActionButton
                            size="sm"
                            tone="secondary"
                            onClick={handleAction(onCopyMarkdown, item)}
                          >
                            Copy Markdown
                          </ActionButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
      {!isLoading && items.length === 0 ? (
        <div className="px-6 py-10 text-center text-brown-600">
          <Typography variant="subtitle1">No media found</Typography>
          <Typography variant="body2" className="mt-2 text-brown-500">
            Try adjusting your filters to see more results.
          </Typography>
        </div>
      ) : null}
    </div>
  );
}

export default MediaTable;
